import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/auth";
import { useDynamicEngine } from "@/lib/dynamicEngine";
import NexWordmark from "@/components/NexWordmark";
import { ArrowLeft, Eye, EyeOff, ArrowUp, ArrowDown, Save, RotateCcw, Loader2, Layers } from "lucide-react";
import { toast } from "sonner";

/**
 * Registry of every scope that supports dynamic layout.
 * The `sections` array is the CANONICAL default order + labels — code stays the
 * source of truth for what sections exist; the DB just holds order + hidden.
 */
const SCOPES = [
  {
    key: "admin_dashboard",
    label: "Admin Dashboard",
    href: "/admin",
    sections: [
      { id: "kpi_strip",       label: "KPI strip (leads · retainer · deploy)" },
      { id: "leads_table",     label: "Leads table" },
      { id: "filters_bar",     label: "Filters + search bar" },
      { id: "bulk_actions",    label: "Bulk actions toolbar" },
    ],
  },
  {
    key: "contact_detail",
    label: "Contact Detail page",
    href: "/admin/contacts",
    sections: [
      { id: "contact_card",    label: "Contact card (name · email · stage)" },
      { id: "tags_block",      label: "Tags block" },
      { id: "note_block",      label: "Add-note block" },
      { id: "action_links",    label: "Micro-landing (ActionLinks) section" },
      { id: "files_block",     label: "Files (Document Center) section" },
      { id: "timeline_block",  label: "Unified timeline" },
    ],
  },
  {
    key: "finance_bills",
    label: "Finance · Bills page",
    href: "/admin/finance/bills",
    sections: [
      { id: "summary_strip",   label: "Summary strip (pending · overdue · paid)" },
      { id: "bank_accounts",   label: "Bank accounts grid" },
      { id: "bills_table",     label: "Bills table" },
    ],
  },
];

const LayoutEditorPage = () => {
  const [scopeKey, setScopeKey] = useState(SCOPES[0].key);
  const scope = useMemo(() => SCOPES.find((s) => s.key === scopeKey), [scopeKey]);
  const { reloadLayout } = useDynamicEngine();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/admin/layout/${scopeKey}`);
      const savedById = new Map((data.items || []).map((r) => [r.section_id, r]));
      const merged = scope.sections.map((s, idx) => {
        const row = savedById.get(s.id);
        return {
          section_id: s.id,
          label: s.label,
          order: row ? row.order : idx,
          hidden: row ? !!row.hidden : false,
        };
      }).sort((a, b) => a.order - b.order);
      setRows(merged);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, [scopeKey, scope]);

  useEffect(() => { load(); }, [load]);

  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    setRows((prev) => {
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((r, i) => ({ ...r, order: i }));
    });
  };

  const toggleHidden = (idx) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, hidden: !r.hidden } : r));
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        items: rows.map((r, i) => ({ section_id: r.section_id, order: i, hidden: r.hidden })),
      };
      await apiClient.put(`/admin/layout/${scopeKey}`, payload);
      await reloadLayout(scopeKey);
      toast.success("Layout saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset this page to the code-defined default order and visibility?")) return;
    setBusy(true);
    try {
      await apiClient.put(`/admin/layout/${scopeKey}`, { items: [] });
      await reloadLayout(scopeKey);
      await load();
      toast.success("Reset to defaults");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <div className="border-b border-nex-border bg-black/40 backdrop-blur">
        <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-4 flex items-center gap-4">
          <Link to="/admin" className="flex items-center gap-2 text-nex-chrome hover:text-nex-gold">
            <ArrowLeft size={14} /> <span className="mono-body text-[11px] uppercase tracking-[0.2em]">Admin</span>
          </Link>
          <span className="text-nex-muted">·</span>
          <NexWordmark size="text-lg" />
          <div className="ml-auto flex items-center gap-2 text-nex-chrome text-xs">
            <Layers size={14} className="text-nex-gold" />
            <span className="mono-body uppercase tracking-[0.2em] text-[11px]">Dynamic Layout</span>
          </div>
        </div>
      </div>

      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-8" data-testid="layout-editor">
        <div className="card-industrial corner-tick p-6 mb-6">
          <div className="mono-label mb-2">// SCOPE</div>
          <h1 className="serif-head text-3xl mb-4">Modular layout editor</h1>
          <p className="text-nex-chrome text-sm mb-4 max-w-2xl">
            Reorder or hide any section on the pages below. Changes apply live for
            every partner. Reset any page back to its code-defined defaults at any time.
          </p>
          <select
            value={scopeKey}
            onChange={(e) => setScopeKey(e.target.value)}
            className="input-industrial w-full max-w-md"
            data-testid="layout-scope-select"
          >
            {SCOPES.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="card-industrial corner-tick p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="mono-label mb-1">// SECTIONS · {scope.label.toUpperCase()}</div>
              <a href={scope.href} target="_blank" rel="noopener noreferrer" className="mono-body text-[11px] text-nex-gold hover:underline">
                Open {scope.href} ↗
              </a>
            </div>
            <div className="flex gap-2">
              <button onClick={resetDefaults} disabled={busy} className="tick-btn-secondary text-xs !py-2 !px-3" data-testid="layout-reset">
                <RotateCcw size={13} /> Reset
              </button>
              <button onClick={save} disabled={busy} className="tick-btn-primary text-xs !py-2 !px-3" data-testid="layout-save">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <><Save size={13} /> Save</>}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10 text-nex-muted">
              <Loader2 className="animate-spin" />
            </div>
          ) : (
            <ul className="space-y-2" data-testid="layout-sections-list">
              {rows.map((r, idx) => (
                <li
                  key={r.section_id}
                  className={`border border-nex-border p-3 flex items-center gap-3 ${r.hidden ? "opacity-50" : ""}`}
                  data-testid={`layout-row-${r.section_id}`}
                >
                  <span className="mono-body text-[10px] text-nex-muted w-8">{String(idx + 1).padStart(2, "0")}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-nex-silver text-sm">{r.label}</div>
                    <div className="mono-body text-[10px] text-nex-muted mt-0.5">{r.section_id}</div>
                  </div>
                  <button
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="text-nex-chrome hover:text-nex-gold disabled:opacity-30 p-1"
                    title="Move up"
                    data-testid={`layout-up-${r.section_id}`}
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    onClick={() => move(idx, 1)}
                    disabled={idx === rows.length - 1}
                    className="text-nex-chrome hover:text-nex-gold disabled:opacity-30 p-1"
                    title="Move down"
                    data-testid={`layout-down-${r.section_id}`}
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button
                    onClick={() => toggleHidden(idx)}
                    className={`p-1 ${r.hidden ? "text-red-300" : "text-nex-chrome hover:text-nex-gold"}`}
                    title={r.hidden ? "Show" : "Hide"}
                    data-testid={`layout-toggle-${r.section_id}`}
                  >
                    {r.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default LayoutEditorPage;
